/**
 * Topics Metadata Resolver for Event Tracking
 *
 * Determines whether the current page is a high-yield topic or regular topic
 * and extracts the appropriate metadata for event payload enrichment.
 */

import { TagNode, TagLocation, TagType, BonsaiTree } from '@mhe/reader/models';
import { TopicTocReverseMap, findTopicTocReverseMapEntry } from './state/topic-toc-reverse-map';

type TopicsTreeNode = TagNode | Record<string, unknown>;

export interface TopicResolutionHints {
  targetId?: string
  targetTitle?: string
  targetCfi?: string
  targetSpineId?: string
  /** Anchor fragment ID from TOC / hash navigation (matches TagLocation.anchorUuid) */
  targetAnchorUuid?: string
  /** Linear spine position index from SpineItem.index; used as nearest-preceding fallback */
  currentSpineIndex?: number
}

/**
 * Metadata for click events on First Aid content
 */
export interface FirstAidTopicMetadata {
  isFirstAidContent: boolean
  isHighYieldTopic: boolean
  contentHighYieldTopic?: string
  contentName?: string
  contentCategory?: string
  topicUuid?: string
  spineId?: string
}

export function resolveMatchedTopicNode(
  topicsTree: unknown,
  selectedLocation: TagLocation | null,
  currentSpineId: string,
  hints?: TopicResolutionHints,
  reverseMap?: TopicTocReverseMap,
): TagNode | null {
  const normalizedTopicsTree = (topicsTree ?? {}) as BonsaiTree<TopicsTreeNode>;

  if (!normalizedTopicsTree || Object.keys(normalizedTopicsTree).length === 0) {
    return null;
  }

  return (
    findNodeByReverseMap(normalizedTopicsTree, reverseMap, selectedLocation, currentSpineId, hints) ||
    (selectedLocation
      ? (findNodeByHints(normalizedTopicsTree, hints) ||
        findNodeByLocation(
          normalizedTopicsTree,
          selectedLocation.cfi,
          selectedLocation.spineId,
          selectedLocation.title,
        ))
      : findNodeByHints(normalizedTopicsTree, hints))
  );
}

/**
 * Resolves topic metadata from the Topics tree based on current location
 *
 * @param topicsTree - The complete topics/DFA tree structure
 * @param selectedLocation - The current selected location (TagLocation)
 * @param currentSpineId - The current spine item ID
 * @returns Metadata object with high-yield/topic distinction and names
 *
 * @example
 * const metadata = resolveFirstAidTopicMetadata(tree, location, spineId);
 * if (metadata.isHighYieldTopic) {
 *   console.log('High-yield topic:', metadata.contentHighYieldTopic);
 * } else if (metadata.isFirstAidContent) {
 *   console.log('Regular topic:', metadata.contentName);
 * }
 */
export function resolveFirstAidTopicMetadata(
  topicsTree: unknown,
  selectedLocation: TagLocation | null,
  currentSpineId: string,
  hints?: TopicResolutionHints,
  reverseMap?: TopicTocReverseMap,
): FirstAidTopicMetadata {
  const normalizedTopicsTree = (topicsTree ?? {}) as BonsaiTree<TopicsTreeNode>;
  const currentNode = resolveMatchedTopicNode(
    topicsTree,
    selectedLocation,
    currentSpineId,
    hints,
    reverseMap,
  );

  if (!currentNode) {
    return {
      isFirstAidContent: false,
      isHighYieldTopic: false,
    };
  }

  const parentNode = findNodeByUuid(normalizedTopicsTree, currentNode.parent);

  const isHighYieldTopic = parentNode?.type === TagType.SUBJECT;

  const categoryNode = isHighYieldTopic
    ? parentNode
    : findTopicCategory(normalizedTopicsTree, currentNode);

  const metadata: FirstAidTopicMetadata = {
    isFirstAidContent: true,
    isHighYieldTopic,
    spineId: currentSpineId,
    topicUuid: currentNode.uuid,
  };

  if (isHighYieldTopic && parentNode) {
    metadata.contentHighYieldTopic = parentNode.name || parentNode.title;
    metadata.contentCategory = parentNode.name || parentNode.title;
  } else if (categoryNode) {
    metadata.contentName = currentNode.name || currentNode.title || selectedLocation?.title;
    metadata.contentCategory = categoryNode.name || categoryNode.title;
  } else {
    metadata.contentName = currentNode.name || currentNode.title || selectedLocation?.title;
  }

  return metadata;
}

/**
 * Finds a topic node by matching the spine ID or location title
 */
function findNodeByLocation(
  topicsTree: BonsaiTree<TopicsTreeNode>,
  cfi?: string,
  spineId?: string,
  title?: string,
): TagNode | null {
  const nodes = Object.values(topicsTree).filter(
    (node): node is TagNode => node && typeof node === 'object' && 'locations' in node,
  );

  for (const node of nodes) {
    if (!node.locations) continue;

    const matchedLocation = node.locations.find(
      (location) => {
        if (cfi) {
          return location.cfi === cfi;
        }

        if (spineId && title) {
          return (
            location.spineId === spineId &&
            titlesRoughlyMatch(location.title, title)
          );
        }

        return false;
      },
    );

    if (matchedLocation) {
      return node;
    }
  }

  return null;
}

/**
 * Finds a node by UUID
 */
function findNodeByUuid(tree: BonsaiTree<TopicsTreeNode>, uuid?: string): TagNode | null {
  if (!uuid || !tree[uuid]) {
    return null;
  }

  const node = tree[uuid];
  return node && typeof node === 'object' && 'name' in node ? (node as TagNode) : null;
}

/**
 * Recursively finds the top-level subject/category node for a given topic node
 */
function findTopicCategory(
  topicsTree: BonsaiTree<TopicsTreeNode>,
  node: TagNode,
  depth = 0,
): TagNode | null {
  if (depth > 10) {
    return null;
  }

  if (node.type === TagType.SUBJECT) {
    return node;
  }

  if (!node.parent) {
    return null;
  }

  const parentNode = findNodeByUuid(topicsTree, node.parent);
  if (!parentNode) {
    return null;
  }

  // The category we want is the nearest ancestor directly under the top-level subject.
  // For a regular FA4 topic, that is the immediate category/high-yield section parent,
  // not the subject node above it.
  if (parentNode.type === TagType.SUBJECT) {
    return node;
  }

  return findTopicCategory(topicsTree, parentNode, depth + 1);
}

/**
 * Builds event tracker metadata object for click events on First Aid content
 *
 * This is the integration point for EventTracker to enrich payloads
 * with topic information when navigating in First Aid ePubs.
 *
 * @example
 * const metadata = buildEventTrackerMetadata(topicsTree, location, spineId);
 * const payload = {
 *   dataAction: 'view_book_reader',
 *   metadata: metadata   // Contains content_high_yield_topic, content_name, etc.
 * };
 */
export function buildEventTrackerMetadata(
  topicsTree: unknown,
  selectedLocation: TagLocation | null,
  currentSpineId: string,
  hints?: TopicResolutionHints,
  reverseMap?: TopicTocReverseMap,
): Record<string, string | number | boolean | null | undefined> {
  const resolvedTopicMetadata = resolveFirstAidTopicMetadata(
    topicsTree || {},
    selectedLocation,
    currentSpineId,
    hints,
    reverseMap,
  );

  const eventMetadata: Record<string, string | number | boolean | null | undefined> = {};

  if (resolvedTopicMetadata.isFirstAidContent) {
    if (resolvedTopicMetadata.isHighYieldTopic) {
      eventMetadata.content_high_yield_topic = resolvedTopicMetadata.contentHighYieldTopic;
    } else if (resolvedTopicMetadata.contentName) {
      eventMetadata.content_name = resolvedTopicMetadata.contentName;
    }

    if (resolvedTopicMetadata.contentCategory) {
      eventMetadata.content_category = resolvedTopicMetadata.contentCategory;
    }
  }

  return eventMetadata;
}

function findNodeByReverseMap(
  topicsTree: BonsaiTree<TopicsTreeNode>,
  reverseMap: TopicTocReverseMap | undefined,
  selectedLocation: TagLocation | null,
  currentSpineId: string,
  hints?: TopicResolutionHints,
): TagNode | null {
  const entry = findTopicTocReverseMapEntry(
    reverseMap,
    selectedLocation,
    currentSpineId,
    hints,
  );

  if (!entry?.topicId) {
    return null;
  }

  return findNodeByUuid(topicsTree, entry.topicId);
}

function findNodeByHints(
  topicsTree: BonsaiTree<TopicsTreeNode>,
  hints?: TopicResolutionHints,
): TagNode | null {
  if (!hints) {
    return null;
  }

  if (hints.targetId) {
    const byId = findNodeByUuid(topicsTree, hints.targetId);
    if (byId) {
      return byId;
    }
  }

  if (hints.targetAnchorUuid) {
    const byAnchor = findNodeByAnchorUuid(topicsTree, hints.targetAnchorUuid);
    if (byAnchor) {
      return byAnchor;
    }
  }

  if (hints.targetCfi || hints.targetSpineId || hints.targetTitle) {
    const byLocation = findNodeByLocation(
      topicsTree,
      hints.targetCfi,
      hints.targetSpineId,
      hints.targetTitle,
    );
    if (byLocation) {
      return byLocation;
    }
  }

  return null;
}

/**
 * Finds a node by matching TagLocation.anchorUuid — used for hash-based TOC navigation.
 */
function findNodeByAnchorUuid(
  topicsTree: BonsaiTree<TopicsTreeNode>,
  anchorUuid: string,
): TagNode | null {
  const nodes = Object.values(topicsTree).filter(
    (node): node is TagNode => node && typeof node === 'object' && 'locations' in node,
  );

  for (const node of nodes) {
    if (node.locations?.find((loc) => loc.anchorUuid === anchorUuid)) {
      return node;
    }
  }

  return null;
}

function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ');
}

function titlesRoughlyMatch(left: string, right: string): boolean {
  const normalizedLeft = normalizeText(left);
  const normalizedRight = normalizeText(right);

  if (!normalizedLeft || !normalizedRight) {
    return false;
  }

  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.includes(normalizedRight) ||
    normalizedRight.includes(normalizedLeft)
  );
}
